import { createId } from "../lib/utils";
import { personaService } from "./persona-service";
import { buildPersonaPrompt } from "../lib/prompt/persona-builder";
import type { ChatMessage, StreamRequest, MessageContent } from "../types/session";

const DEEPSEEK_API_URL = "https://api.deepseek.com/chat/completions";
const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions";
const QWEN_API_URL = "https://dashscope.aliyuncs.com/api/v1/services/aigc/multimodal-generation/generation";
const GLM_API_URL = "https://open.bigmodel.cn/api/paas/v4/chat/completions";

// 模型映射
const MODEL_MAP: Record<string, string> = {
  "deepseek-v3": "deepseek-chat",
  "deepseek-v2": "deepseek-chat",
  "hunyuan": "deepseek-chat", // 暂时映射到 deepseek，后续可接入混元
  "gpt-4o": "gpt-4o",
  "gpt-4o-mini": "gpt-4o-mini",
  "qwen-vl-max": "qwen-vl-max",
  "glm-4v": "glm-4v",
};

// 支持多模态的模型列表
const MULTIMODAL_MODELS = ["gpt-4o", "gpt-4o-mini", "qwen-vl-max", "glm-4v"];

export const chatService = {
  async streamReply(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const modelName = MODEL_MAP[request.model] || "deepseek-chat";
    const isMultimodal = MULTIMODAL_MODELS.includes(request.model);

    // 检查是否包含图片
    const hasImage = request.messages.some(msg => 
      typeof msg.content !== "string" && Array.isArray(msg.content)
    );

    // 如果有图片但模型不支持多模态,提示用户
    if (hasImage && !isMultimodal) {
      await this.mockStreamReply(
        request,
        (chunk) => onChunk(chunk),
        signal
      );
      onChunk("\n\n⚠️ 当前模型不支持图片理解,请切换到 GPT-4o、通义千问 VL 或 GLM-4V 等多模态模型。");
      return;
    }

    // 根据模型选择不同的 API
    if (request.model.startsWith("gpt-4")) {
      await this.streamOpenAI(request, onChunk, signal);
    } else if (request.model === "qwen-vl-max") {
      await this.streamQwenVL(request, onChunk, signal);
    } else if (request.model === "glm-4v") {
      await this.streamGLM4V(request, onChunk, signal);
    } else {
      // 默认 DeepSeek
      await this.streamDeepSeek(request, onChunk, signal);
    }
  },

  // DeepSeek API (仅文本)
  async streamDeepSeek(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY;
    console.log("=== DeepSeek API Debug ===");
    console.log("API Key:", apiKey ? `Found (length: ${apiKey.length})` : "NOT FOUND");

    if (!apiKey) {
      console.warn("DeepSeek API Key not configured, using mock response");
      await this.mockStreamReply(request, onChunk, signal);
      return;
    }

    const persona = await personaService.getById(request.personaId);
    let systemPrompt = buildPersonaPrompt(persona);
    
    // 如果有上下文信息，添加到系统提示中
    if (request.context) {
      systemPrompt += `\n\n以下是其他角色的对话历史，供你参考：${request.context}`;
    }
    
    const modelName = MODEL_MAP[request.model] || "deepseek-chat";

    const messages = [
      { role: "system", content: systemPrompt },
      ...request.messages.map((msg) => ({
        role: msg.role,
        content: typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content),
      })),
    ];

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: modelName,
          messages,
          stream: true,
          temperature: 0.7,
          max_tokens: 4096,
        }),
        signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`DeepSeek API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === "data: [DONE]") continue;
          if (!trimmed.startsWith("data: ")) continue;

          try {
            const jsonStr = trimmed.slice(6);
            const json = JSON.parse(jsonStr);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              console.log("chunk:", content);
              onChunk(content);
            }
          } catch (e) {
            console.warn("Parse error:", trimmed, e);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      console.error("DeepSeek API error:", error);
      await this.mockStreamReply(request, onChunk, signal);
    }
  },

  // OpenAI GPT-4 Vision API
  async streamOpenAI(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    console.log("=== OpenAI API Debug ===");
    console.log("API Key:", apiKey ? `Found (length: ${apiKey.length})` : "NOT FOUND");

    if (!apiKey) {
      console.warn("OpenAI API Key not configured");
      await this.mockStreamReply(request, onChunk, signal);
      onChunk("\n\n⚠️ 请配置 VITE_OPENAI_API_KEY 环境变量以使用 GPT-4 Vision。");
      return;
    }

    const persona = await personaService.getById(request.personaId);
    let systemPrompt = buildPersonaPrompt(persona);
    
    // 如果有上下文信息，添加到系统提示中
    if (request.context) {
      systemPrompt += `\n\n以下是其他角色的对话历史，供你参考：${request.context}`;
    }
    
    const modelName = MODEL_MAP[request.model] || "gpt-4o";

    const messages = [
      { role: "system", content: systemPrompt },
      ...request.messages.map((msg) => {
        // 如果是字符串,直接返回
        if (typeof msg.content === "string") {
          return { role: msg.role, content: msg.content };
        }
        // 如果是数组(多模态),直接传递
        return { role: msg.role, content: msg.content };
      }),
    ];

    try {
      const response = await fetch(OPENAI_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: modelName,
          messages,
          stream: true,
          temperature: 0.7,
          max_tokens: 4096,
        }),
        signal,
      });

      if (!response.ok || !response.body) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenAI API error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === "data: [DONE]") continue;
          if (!trimmed.startsWith("data: ")) continue;

          try {
            const jsonStr = trimmed.slice(6);
            const json = JSON.parse(jsonStr);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch (e) {
            console.warn("Parse error:", trimmed, e);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      console.error("OpenAI API error:", error);
      await this.mockStreamReply(request, onChunk, signal);
      onChunk(`\n\n❌ API 调用失败: ${(error as Error).message}`);
    }
  },

  // 通义千问 VL API
  async streamQwenVL(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const apiKey = import.meta.env.VITE_QWEN_API_KEY;
    console.log("=== Qwen VL API Debug ===");
    console.log("API Key:", apiKey ? `Found (length: ${apiKey.length})` : "NOT FOUND");

    if (!apiKey) {
      console.warn("Qwen API Key not configured");
      await this.mockStreamReply(request, onChunk, signal);
      onChunk("\n\n⚠️ 请配置 VITE_QWEN_API_KEY 环境变量以使用通义千问 VL。");
      return;
    }

    const persona = await personaService.getById(request.personaId);
    let systemPrompt = buildPersonaPrompt(persona);
    
    // 如果有上下文信息，添加到系统提示中
    if (request.context) {
      systemPrompt += `\n\n以下是其他角色的对话历史，供你参考：${request.context}`;
    }

    // 构建消息内容
    const messages = [];
    for (const msg of request.messages) {
      if (typeof msg.content === "string") {
        messages.push({ role: msg.role, content: msg.content });
      } else if (Array.isArray(msg.content)) {
        // 通义千问的多模态格式
        const content = msg.content.map(item => {
          if (item.type === "text") {
            return { text: item.text };
          } else if (item.type === "image_url") {
            return { image: item.image_url.url };
          }
          return item;
        });
        messages.push({ role: msg.role, content });
      }
    }

    try {
      const response = await fetch(QWEN_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
          "X-DashScope-SSE": "enable",
        },
        body: JSON.stringify({
          model: "qwen-vl-max",
          input: {
            messages: [
              { role: "system", content: systemPrompt },
              ...messages,
            ],
          },
          parameters: {
            temperature: 0.7,
            max_tokens: 4096,
          },
        }),
        signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Qwen API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data:")) continue;

          try {
            const jsonStr = trimmed.slice(5).trim();
            const json = JSON.parse(jsonStr);
            const content = json.output?.choices?.[0]?.message?.content;
            if (content) {
              onChunk(content);
            }
          } catch (e) {
            console.warn("Parse error:", trimmed, e);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      console.error("Qwen API error:", error);
      await this.mockStreamReply(request, onChunk, signal);
      onChunk(`\n\n❌ API 调用失败: ${(error as Error).message}`);
    }
  },

  // 智谱 GLM-4V API
  async streamGLM4V(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const apiKey = import.meta.env.VITE_GLM_API_KEY;
    console.log("=== GLM-4V API Debug ===");
    console.log("API Key:", apiKey ? `Found (length: ${apiKey.length})` : "NOT FOUND");

    if (!apiKey) {
      console.warn("GLM API Key not configured");
      await this.mockStreamReply(request, onChunk, signal);
      onChunk("\n\n⚠️ 请配置 VITE_GLM_API_KEY 环境变量以使用 GLM-4V。");
      return;
    }

    const persona = await personaService.getById(request.personaId);
    let systemPrompt = buildPersonaPrompt(persona);
    
    // 如果有上下文信息，添加到系统提示中
    if (request.context) {
      systemPrompt += `\n\n以下是其他角色的对话历史，供你参考：${request.context}`;
    }

    const messages = [
      { role: "system", content: systemPrompt },
      ...request.messages.map((msg) => {
        if (typeof msg.content === "string") {
          return { role: msg.role, content: msg.content };
        }
        return { role: msg.role, content: msg.content };
      }),
    ];

    try {
      const response = await fetch(GLM_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "glm-4v",
          messages,
          stream: true,
          temperature: 0.7,
          max_tokens: 4096,
        }),
        signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`GLM API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === "data: [DONE]") continue;
          if (!trimmed.startsWith("data: ")) continue;

          try {
            const jsonStr = trimmed.slice(6);
            const json = JSON.parse(jsonStr);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch (e) {
            console.warn("Parse error:", trimmed, e);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      console.error("GLM API error:", error);
      await this.mockStreamReply(request, onChunk, signal);
      onChunk(`\n\n❌ API 调用失败: ${(error as Error).message}`);
    }
  },

  async mockStreamReply(request: StreamRequest, onChunk: (value: string) => void, signal?: AbortSignal) {
    const persona = await personaService.getById(request.personaId);
    const lastUserMessageContent = [...request.messages].reverse().find((item) => item.role === "user")?.content ?? "";
    const mockText = this.createMockReply(lastUserMessageContent, buildPersonaPrompt(persona));
    
    const segments = mockText.split(/(?<=。)|(?<=\n)|(?<=：)|(?<=, )/).filter(Boolean);
    for (const segment of segments) {
      if (signal?.aborted) return;
      onChunk(segment);
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
  },

  createMockReply(userInput: MessageContent, personaPrompt: string) {
    // 提取文本内容
    const textContent = typeof userInput === "string" 
      ? userInput 
      : Array.isArray(userInput) 
        ? userInput.find(item => item.type === "text")?.text || ""
        : "";

    // 提取人格名称
    const personaMatch = personaPrompt.match(/你是([^，。]+)/);
    const personaName = personaMatch ? personaMatch[1] : "AI 助手";
    
    // 提取开场白
    const openerMatch = personaPrompt.match(/开场白[：:]\s*"([^"]+)"/);
    const opener = openerMatch ? openerMatch[1] : "你好！有什么可以帮你的吗？";
    
    return [
      opener,
      "",
      "（当前为 Mock 模式，请配置 API Key 以使用真实 AI 回复）",
    ].join("\n");
  },
  createOptimisticUserMessage(content: MessageContent): ChatMessage {
    return {
      id: createId("msg"),
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
  },
  createAssistantPlaceholder(model: StreamRequest["model"]): ChatMessage {
    return {
      id: createId("msg"),
      role: "assistant",
      content: "",
      createdAt: new Date().toISOString(),
      model,
    };
  },
};
